/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import * as yup from "yup";

/* MATERIAL TAILWIND COMPONENTS */
import { Button } from "@material-tailwind/react";

/* TOAST NOTIFICATION */
import { toast } from "react-toast";

/* USER DEFINED COMPONENTS */
import BlogForm from "./blog-form";

/* API IMPORT */
import { editBlogById, getBlogById } from "../../../service/api";

const validationSchema = yup.object().shape({
  title: yup.string().required("Article title is required"),
  summary: yup.string().required("Short summary is required"),
  content: yup
    .string()
    .test(
      "not-empty",
      "Content is required",
      (value) => value && value.replace(/<(.|\n)*?>/g, "").trim().length > 0
    )
    .required("Content is required"),
});

const EditBlog = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  const formik = useFormik({
    initialValues: {
      title: "",
      slug: "",
      category: "General",
      categoryBadge: "Health & Wellness",
      summary: "",
      content: "",
      readTime: "5 min read",
      author: "WellCare Clinical Team",
      sortOrder: 0,
      seoTitle: "",
      seoDescription: "",
      isActive: true,
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      const body = {
        title: values.title,
        slug: values.slug,
        category: values.category,
        categoryBadge: values.categoryBadge,
        summary: values.summary,
        content: values.content,
        readTime: values.readTime,
        author: values.author,
        seoTitle: values.seoTitle,
        seoDescription: values.seoDescription,
        isActive: values.isActive,
      };
      if (values.sortOrder !== "" && values.sortOrder !== null) {
        body.sortOrder = Number(values.sortOrder);
      }

      setLoading(true);
      editBlogById(id, body)
        .then((res) => {
          if (res?.status) {
            toast.success(res?.message || "Health blog updated successfully");
            navigate("/blogs");
          } else {
            toast.error(res?.message || "Failed to update blog article");
          }
        })
        .catch((error) => {
          toast.error(
            error?.response?.data?.message || "Failed to update health blog article"
          );
        })
        .finally(() => {
          setLoading(false);
        });
    },
  });

  useEffect(() => {
    if (id) {
      setFetching(true);
      getBlogById(id)
        .then((res) => {
          if (res?.status && res?.data) {
            const b = res.data;
            formik.setValues({
              title: b.title || "",
              slug: b.slug || "",
              category: b.category || "General",
              categoryBadge: b.categoryBadge || "Health & Wellness",
              summary: b.summary || "",
              content: b.content || "",
              readTime: b.readTime || "5 min read",
              author: b.author || "WellCare Clinical Team",
              sortOrder: b.sortOrder || 0,
              seoTitle: b.seoTitle || "",
              seoDescription: b.seoDescription || "",
              isActive: b.isActive !== undefined ? b.isActive : true,
            });
          }
        })
        .catch((e) => {
          toast.error("Failed to fetch blog article details");
        })
        .finally(() => {
          setFetching(false);
        });
    }
  }, [id]);

  if (fetching) {
    return (
      <div className="p-10 text-center">
        <p className="text-base font-ibm-semibold text-black">
          Loading blog article details...
        </p>
      </div>
    );
  }

  return (
    <section className="p-5 min-h-screen xl:min-h-full lg:min-h-full">
      {/* PAGE TITLE */}
      <Helmet>
        <title>Edit Health Blog | WellCare Pharmacy Admin</title>
      </Helmet>

      <form onSubmit={formik.handleSubmit}>
        <div className="container-full-width">
          <div className="block xl:flex lg:flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-ibm-bold text-black tracking-tight">
                Edit Health Blog
              </h1>
              <h2 className="text-base xl:text-lg lg:text-lg font-ibm-regular text-greyText tracking-tight">
                Update the article content and settings.
              </h2>
            </div>
            <div className="flex items-center gap-2 mt-2 xl:mt-0 lg:mt-0">
              <Link to="/blogs">
                <Button className="w-full xl:w-auto lg:w-auto whitespace-nowrap shadow-none hover:shadow-none bg-white hover:bg-black hover:bg-opacity-5 px-5 py-3 rounded-md border border-authBorder text-sm font-ibm-semibold text-black normal-case transition-all duration-300 ease-in-out">
                  Back to Blogs
                </Button>
              </Link>
              <Button
                type="submit"
                disabled={loading}
                className="w-full xl:w-auto lg:w-auto whitespace-nowrap shadow-none hover:shadow-none bg-logoYellow hover:bg-opacity-80 px-5 py-3 rounded-md border border-logoYellow text-sm font-ibm-semibold text-white normal-case transition-all duration-300 ease-in-out"
              >
                {loading ? "Please wait..." : "Save Changes"}
              </Button>
            </div>
          </div>

          <div className="mt-6">
            <BlogForm formik={formik} />
          </div>
        </div>
      </form>
    </section>
  );
};

export default EditBlog;
